'use client';
import { useState } from 'react';
import { useTranslations as useTranslations$1, useLocale } from 'next-intl';
import { ChevronRight, ExternalLink } from '@/components/ui/icons';
import { TrackedDiscordLink } from '@/components/analytics/TrackedDiscordLink';
import { getLessons } from '@/app/(app)/learn/learn-data';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { getSectionWithPages, docPath, toDocsLocale } from '@/lib/docs';
import { LEARN_SIDEBAR_COPY } from '@/app/(app)/learn/learn-copy';
import { usePublishedOverrides } from '@/i18n/useContentOverrides';
import { applyLearnSidebarCopyOverrides, applyLessonOverrides } from '@/lib/i18n/learnRouteMessages';
import { overrideDocsTitle } from '@/lib/i18n/docsRouteMessages';
interface SidebarLink {
    slug: string;
    title: string;
    href: string;
}
export const SIDEBAR_GROUPS = [
    {
        // i18n-ignore -- dictionary key into copy.groups, not display text
        label: 'Using Martini',
        sectionIds: [
            'getting-started',
            'canvas',
            'generation',
            'library',
            'sets',
            'timeline',
            'export'
        ]
    },
    {
        // i18n-ignore -- dictionary key into copy.groups, not display text
        label: 'Your Workspace',
        sectionIds: [
            'collaboration',
            'teams',
            'account'
        ]
    },
    {
        // i18n-ignore -- dictionary key into copy.groups, not display text
        label: 'Developers',
        sectionIds: [
            'developers'
        ]
    },
    {
        label: 'Reference',
        sectionIds: []
    }
] as const;
function rowClass(active: boolean) {
    return `rounded-md px-2 py-1.5 transition-colors ${active ? 'bg-secondary/50 text-foreground' : 'text-muted-foreground hover:text-foreground'}`;
}
function SidebarSection({ title, links, currentPath, defaultOpen }: {
    title: string;
    links: SidebarLink[];
    currentPath: string;
    defaultOpen: boolean;
}) {
    const [open, setOpen] = useState(defaultOpen);
    return (<div>
      <button onClick={()=>setOpen(!open)} className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-start font-medium text-muted-foreground transition-colors hover:text-foreground">
        <ChevronRight className={`h-3 w-3 shrink-0 text-muted-foreground/50 transition-transform ${open ? 'rotate-90' : ''}`}/>
        {title}
      </button>

      { /* hidden, not unmounted: every article link ships in the server HTML,
          so crawlers see the full docs link graph, not just the open section. */ }
      <div hidden={!open} className="ms-2 flex flex-col gap-0.5 border-s border-border/40 ps-3 pt-0.5">
        {links.map((link)=>(<Link key={link.slug} href={link.href} className={rowClass(currentPath === link.href)}>
            {link.title}
          </Link>))}
      </div>
    </div>);
}
function SidebarGroupLabel({ label }: {
    label: string;
}) {
    return (<div className="px-2 pb-1 pt-5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/40 first:pt-3">
      {label}
    </div>);
}
// Brand name — verbatim in every locale.
const DISCORD_LABEL = 'Discord';
/** External link to the Discord community — shown in both sidebar navs. */ function DiscordRow() {
    return (<TrackedDiscordLink source="learn_sidebar" className={`${rowClass(false)} flex items-center gap-1.5`}>
      {DISCORD_LABEL}
      <ExternalLink className="h-3 w-3 text-muted-foreground/40"/>
    </TrackedDiscordLink>);
}
// ─── Tutorial nav (shown on /learn/tutorials and the lesson + community pages) ──
function TutorialNav({ currentPath }: {
    currentPath: string;
}) {
    const locale = toDocsLocale(useLocale());
    const overrides = usePublishedOverrides(locale);
    const copy = applyLearnSidebarCopyOverrides(LEARN_SIDEBAR_COPY[locale], overrides);
    return (<nav className="flex flex-col gap-0.5 text-[13px]">
      <SidebarGroupLabel label={copy.videoLessons}/>
      <Link href="/learn/tutorials" className={rowClass(currentPath === '/learn/tutorials')}>
        {copy.overview}
      </Link>
      {getLessons(locale).map((l)=>{
        const lesson = applyLessonOverrides(l, overrides);
        const href = `/learn/tutorials/${lesson.slug}`;
        return (<Link key={lesson.slug} href={href} className={rowClass(currentPath === href)}>
            {lesson.title}
          </Link>);
    })}
      <SidebarGroupLabel label={copy.communityVideos}/>
      <Link href="/learn/community" className={rowClass(currentPath === '/learn/community')}>
        {copy.communityVideos}
      </Link>
      <DiscordRow/>
    </nav>);
}
// ─── Docs nav (shown on /learn, doc articles, glossary, shortcuts) ─────────────
function DocsNav({ currentPath }: {
    currentPath: string;
}) {
    const t = useTranslations$1();
    const locale = toDocsLocale(useLocale());
    const overrides = usePublishedOverrides(locale);
    const copy = applyLearnSidebarCopyOverrides(LEARN_SIDEBAR_COPY[locale], overrides);
    const referenceLinks: SidebarLink[] = [
        {
            slug: 'shortcuts',
            title: copy.keyboardShortcuts,
            href: '/learn/shortcuts'
        },
        {
            slug: 'glossary',
            title: copy.glossary,
            href: '/learn/glossary'
        }
    ];
    const docSections = getSectionWithPages(locale);
    const sectionMap = new Map(docSections.map((s)=>[
            s.id,
            s
        ]));
    return (<nav className="flex flex-col gap-0.5 text-[13px]">
      {SIDEBAR_GROUPS.map((group)=>{
        const sections = group.sectionIds.map((id)=>sectionMap.get(id)).filter((s): s is NonNullable<typeof s> =>!!s);
        const isRefGroup = group.label === 'Reference';
        if (!sections.length && !isRefGroup) return null;
        return (<div key={group.label} className="flex flex-col gap-0.5">
            <SidebarGroupLabel label={copy.groups[group.label]}/>
            {isRefGroup && referenceLinks.map((link)=>(<Link key={link.slug} href={link.href} className={rowClass(currentPath === link.href)}>
                  {link.title}
                </Link>))}
            {sections.map((section)=>{
            const links = section.pages.map((p)=>({
                    slug: p.slug,
                    // Pages already sit under their section heading here, so a page
                    // may shorten its nav label via `navTitle` (see DocsPage). A
                    // translation override still wins — it's the localized title.
                    title: overrideDocsTitle(p.slug, p.navTitle ?? p.title, overrides),
                    href: docPath(p.slug)
                }));
            const isSectionActive = links.some((l)=>currentPath === l.href);
            return (<SidebarSection key={section.id} title={section.title} links={links} currentPath={currentPath} defaultOpen={isSectionActive}/>);
        })}
          </div>);
    })}
      <SidebarGroupLabel label={t("4CrCbD")}/>
      <DiscordRow/>
    </nav>);
}
export function LearnSidebar() {
    const pathname = usePathname() ?? '';
    const isTutorialPage = pathname === '/learn/tutorials' || pathname.startsWith('/learn/tutorials/') || pathname === '/learn/community';
    return isTutorialPage ? <TutorialNav currentPath={pathname}/> : <DocsNav currentPath={pathname}/>;
}
